/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage } from 'react-intl';

import './AmountInput.styles.scss';
import RouteNames from '../../../appAuthorized/RouteNames';

import InputWithLabel from '../../../components/InputWithLabel';
import createMedliTerm from '../../../../models/MedliTermCompose';
import InputAlignedText from '../../../components/InputAlignedText';
import InputErrorText from '../../../components/InputErrorText';
import MedliSearchTerm from '../../../components/MedliSearchTerm';
import AppUtils from '../../../../utilities/AppUtils';
import { Card, CardRowUI } from '../../../../bphComponents/bundle';
import FlexRow from '../../../components/FlexRow';
import composeAmount from '../../../../models/HealthEntryCompose/amount';
import MedliTerm from '../../../components/MedliTerm';
import { useNavigate } from '../../../hooks/useNavigate';
import { useActiveUser } from '../../../hooks/useUser';
import { useRelatedUnits, useAmountUnit } from '../../../hooks/useMedli';
import HealthEntryCategoryNames from '../../../../constants/HealthEntryCategoryNames';

interface SelectUnitProps {
  unitId: number;
  quicklistUnits: any[];
  onChangeAmountUnit(unit): void;
  textPhrase: any;
  goToAmountUnits(): void;
}
const SelectUnit: React.FC<SelectUnitProps> = (props) => {
  const hasUnit = !!props.unitId;
  const [isExpanded, setIsExpanded] = React.useState(!hasUnit);

  return (
    <>
      <Card.CardContent hasBottomBorder={true} onClick={() => { setIsExpanded(!isExpanded); }}>
        <Card.CollapsibleControl isExpanded={isExpanded}>
          <div className="font-footnote font-color-profile-accent">
            {
              hasUnit ? (
                <FormattedMessage {...props.textPhrase.changeUnit} />
              ) : (
                <FormattedMessage {...props.textPhrase.addUnit} />
              )
            }
          </div>
        </Card.CollapsibleControl>
      </Card.CardContent>
      <Card.CollapsibleContent isExpanded={isExpanded}>
        {
          props.quicklistUnits.map((u, index) => (
            <CardRowUI.SelectableRow
              key={index}
              onClick={() => {
                props.onChangeAmountUnit(u);
                setIsExpanded(false);
              }}
              selectedClassName="fill-color-profile"
              isSelected={props.unitId === u.id}
              hasBottomBorder={true}
            >
              <MedliSearchTerm term={u} />
            </CardRowUI.SelectableRow>
          ))
        }

        <CardRowUI.TextAction onClick={props.goToAmountUnits}>
          <div className="font-body font-color-profile-accent">
            <FormattedMessage {...props.textPhrase.search} />
          </div>
        </CardRowUI.TextAction>
      </Card.CollapsibleContent>
    </>
  );
};

function useModifiedQuicklistUnits(category, cui, unit, languageCode) {
  // insures current amount is in quicklist, so it can be deselected
  const [{ relatedUnitList }] = useRelatedUnits(category, cui);

  if (!unit || !unit.id) { return relatedUnitList; }

  const list = relatedUnitList.filter(u => (u.cui === unit.cui ? u.id === unit.id : true));

  const foundIndex = list.findIndex(u => u.id === unit.id);
  if (foundIndex === -1) {
    const medliUnit: any = createMedliTerm(unit, languageCode);
    return [medliUnit.convertToSearchTerm()].concat(list);
  }

  return list;
}

interface Props {
  cui: string;
  category: HealthEntryCategoryNames;
  amount: any;
  allowCompositeUnit: boolean;
  onChangeAmount(data): void;
  valueValidation(val): { isValid: boolean; errorMessage?: any; };
  haveInitialFocus: boolean;
  textPhrase: any;
  healthEntryId: number;
  healthEntryValueId: number;
}
const AmountInput: React.FC<Props> = (props) => {
  const { amount, category, allowCompositeUnit, onChangeAmount, valueValidation, haveInitialFocus, textPhrase, healthEntryId, healthEntryValueId, cui } = props;
  const [{ preferredLanguageCode }] = useActiveUser();

  const [errorMessage, setErrorMessage] = React.useState(null);

  const [, navActions] = useNavigate();

  const [{
    unit,
    unitPairs,
    isCompositeUnit,
    showUnit,
  }] = useAmountUnit(amount, allowCompositeUnit);
  const modifiedQuicklistUnits = useModifiedQuicklistUnits(category, cui, unit, preferredLanguageCode);

  const fieldWidth = isCompositeUnit ? 60 : 140;
  function onChangeAmountValue(index, valueData) {
    const newValue = valueData.replace('|', '');
    const amountData = composeAmount({ amount }, preferredLanguageCode);
    amountData.setUnit(unit);

    const values = amountData.getValueArray();
    values[index] = newValue;
    amountData.setValues(values);

    onChangeAmount(amountData.get());
  }

  function onChangeAmountUnit(quicklistUnit) {
    const amountData = composeAmount({ amount }, preferredLanguageCode);
    amountData.setUnit(unit);
    amountData.setValue(unitPairs[0].value);
    amountData.replaceWithSearchTerm(quicklistUnit);
    onChangeAmount(amountData.get());
  }

  function onBlur() {
    const amountData = composeAmount({ amount }, preferredLanguageCode);
    const validation = valueValidation ? valueValidation : () => ({ isValid: true, errorMessage: {} });
    const validData = amountData.hasAmount() ? validation(amountData.getValue()) : { isValid: true, errorMessage: {} };

    setErrorMessage(!validData.isValid ? <FormattedMessage {...validData.errorMessage} /> : null);
  }

  return (
    <Card.CardShape>

    <Card.CardContent hasBottomBorder={true}>
      <FlexRow style={{ justifyContent: 'flex-start', alignItems: 'flex-end' }}>

      {
        unitPairs.map((pair, index) => (
          <React.Fragment key={index}>
            <div style={{ width: fieldWidth, marginRight: 12 }}>
              <InputWithLabel
                value={pair.value}
                onChange={(event) => { onChangeAmountValue(index, event.target.value); }}
                onBlur={onBlur}
                isClearable={!isCompositeUnit}
                clearValue={() => { onChangeAmountValue(index, ''); }}
                onFocus={() => { setErrorMessage(null); }}
                isInvalid={!!errorMessage}
                type={AppUtils.isIosDevice() ? 'text' : 'tel'}
                label={index === 0 && <FormattedMessage {...textPhrase.inputLabel} />}
                name={`amount_value_${index}`}
                initialFocus={haveInitialFocus && !pair.value && index === 0}
                noForcedHeight
              />
            </div>
            <div>
              {
                showUnit && (
                  <div style={{ marginRight: 12 }}>
                    <InputAlignedText>
                      <MedliTerm className="font-subhead" medliTerm={pair.unit} />
                    </InputAlignedText>
                  </div>
                )
              }
            </div>
          </React.Fragment>
        ))
      }

      </FlexRow>
      <InputErrorText>
        {errorMessage}
      </InputErrorText>
    </Card.CardContent>

    <SelectUnit
      unitId={unit.id}
      quicklistUnits={modifiedQuicklistUnits}
      onChangeAmountUnit={onChangeAmountUnit}
      goToAmountUnits={() => {
        if (healthEntryValueId) {
          navActions.goTo(RouteNames.healthEntryValueUnit(healthEntryId, healthEntryValueId));
        } else {
          navActions.goTo(RouteNames.healthEntryAmountUnit(healthEntryId));
        }
      }}
      textPhrase={textPhrase}
    />

    </Card.CardShape>
  );
};

export default AmountInput;
